import { MODE } from '../env';

export type PasswordPolicyConfig = {
  minLength: number;
  maxLength: number;
  requireAlpha: boolean;
  requireNumeric: boolean;
  requireSpecial: boolean;
  requireMixCase: boolean;
  requireOneCriteria: boolean;
};

export const resolvePasswordPolicy = (
  mode: string = MODE
): PasswordPolicyConfig => {
  switch (mode) {
    case 'qa':
      return {
        minLength: 15,
        maxLength: -1,
        requireAlpha: true,
        requireNumeric: true,
        requireSpecial: true,
        requireMixCase: true,
        requireOneCriteria: true,
      };
    case 'test':
      return {
        minLength: 3,
        maxLength: -1,
        requireAlpha: false,
        requireNumeric: false,
        requireSpecial: false,
        requireMixCase: false,
        requireOneCriteria: true,
      };
    case 'development':
      return {
        minLength: 3,
        maxLength: -1,
        requireAlpha: false,
        requireNumeric: false,
        requireSpecial: false,
        requireMixCase: false,
        requireOneCriteria: false,
      };
    case 'production':
    default:
      return {
        minLength: 15,
        maxLength: -1,
        requireAlpha: true,
        requireNumeric: true,
        requireSpecial: true,
        requireMixCase: true,
        requireOneCriteria: false,
      };
  }
};

export const PASSWORD_POLICY = resolvePasswordPolicy();
export const PASSWORD_MIN_LENGTH = PASSWORD_POLICY.minLength;
export const PASSWORD_MAX_LENGTH = PASSWORD_POLICY.maxLength;

export const hasPasswordMinLength = (
  password: string,
  minLength: number = PASSWORD_MIN_LENGTH
) => password.length >= minLength;

export const hasPasswordMaxLength = (
  password: string,
  maxLength: number = PASSWORD_MAX_LENGTH
) => maxLength === -1 || password.length <= maxLength;

export const hasPasswordUppercase = (password: string) => /[A-Z]/.test(password);

export const hasPasswordLowercase = (password: string) => /[a-z]/.test(password);

export const hasPasswordNumber = (password: string) => /\d/.test(password);

export const hasPasswordSpecialCharacter = (password: string) =>
  /[^a-zA-Z0-9 ]/.test(password);

export const hasPasswordMixCase = (password: string) =>
  hasPasswordUppercase(password) && hasPasswordLowercase(password);

export const hasPasswordOneOfContent = (password: string) =>
  hasPasswordNumber(password) ||
  hasPasswordSpecialCharacter(password) ||
  hasPasswordMixCase(password);

export const isPasswordPolicySatisfied = (
  password: string,
  policy: PasswordPolicyConfig = PASSWORD_POLICY
) => {
  if (!hasPasswordMinLength(password, policy.minLength)) {
    return false;
  }

  if (!hasPasswordMaxLength(password, policy.maxLength)) {
    return false;
  }

  if (policy.requireOneCriteria) {
    return hasPasswordOneOfContent(password);
  }

  if (policy.requireAlpha && !/[a-zA-Z]/.test(password)) {
    return false;
  }

  if (policy.requireNumeric && !hasPasswordNumber(password)) {
    return false;
  }

  if (policy.requireSpecial && !hasPasswordSpecialCharacter(password)) {
    return false;
  }

  if (policy.requireMixCase && !hasPasswordMixCase(password)) {
    return false;
  }

  return true;
};

export type PasswordPolicyCheck = {
  key: string;
  label: string;
  isSatisfied: boolean;
  params?: Record<string, string | number>;
};

export const getPasswordPolicyChecks = (
  password: string,
  policy: PasswordPolicyConfig = PASSWORD_POLICY
): PasswordPolicyCheck[] => {
  const checks: PasswordPolicyCheck[] = [
    {
      key: 'length',
      label: 'pwd_length_validation',
      isSatisfied: hasPasswordMinLength(password, policy.minLength),
      params: { count: policy.minLength },
    },
  ];

  if (policy.maxLength !== -1) {
    checks.push({
      key: 'max_length',
      label: 'pwd_max_length_validation',
      isSatisfied: hasPasswordMaxLength(password, policy.maxLength),
      params: { count: policy.maxLength },
    });
  }

  if (policy.requireOneCriteria) {
    checks.push({
      key: 'one_of_content',
      label: 'pwd_one_of_content',
      isSatisfied: hasPasswordOneOfContent(password),
    });

    return checks;
  }

  if (policy.requireMixCase) {
    checks.push({
      key: 'uppercase',
      label: 'pwd_capital_case',
      isSatisfied: hasPasswordUppercase(password),
    });
    checks.push({
      key: 'lowercase',
      label: 'pwd_lowercase',
      isSatisfied: hasPasswordLowercase(password),
    });
  }

  if (policy.requireAlpha && !policy.requireMixCase) {
    checks.push({
      key: 'alpha',
      label: 'pwd_alpha',
      isSatisfied: /[a-zA-Z]/.test(password),
    });
  }

  if (policy.requireNumeric) {
    checks.push({
      key: 'number',
      label: 'pwd_number',
      isSatisfied: hasPasswordNumber(password),
    });
  }

  if (policy.requireSpecial) {
    checks.push({
      key: 'special_character',
      label: 'pwd_special_char',
      isSatisfied: hasPasswordSpecialCharacter(password),
    });
  }

  return checks;
};
